/**
 * ShareKit - Main Entry Point (Manager)
 * A central controller for managing multiple share bar instances.
 * Holds global defaults and provides factory methods for creating instances.
 */

// Global constants injected at build time
declare const __BUILD_TIME__: string;
declare const __VERSION__: string;

import type {
  SocialShareConfig,
  ShareContent,
  PositionMode,
  PlatformId,
  LayoutMode,
  ButtonShape,
  BlockShareConfig,
} from '../types';

/** Configuration for Smart Block responsive layout */
export interface SmartBlockConfig {
  target: string | HTMLElement;
  p1: PlatformId[];
  p2?: PlatformId[];   // Optional, copylink is auto-appended
  threshold?: number;  // Default: 640
  height?: number;     // Default: 50
  gap?: number;        // Default: 2
  radius?: number;     // Default: 0 (Square)
}
import { ShareInstance, ShareInstanceConfig } from './ShareInstance';
import { DEFAULT_CONFIG, DEFAULT_LABELS } from './config';

/** Global configuration options for ShareKit */
export interface ShareKitGlobalConfig {
  /** Default platforms for all instances */
  platforms?: PlatformId[];
  /** Global content defaults */
  content?: ShareContent;
  /** Default icon sprite path */
  iconSpritePath?: string;
  /** Default icon size */
  iconSize?: number;
  /** Default button opacity */
  buttonOpacity?: number;
  /** Default button margin */
  buttonMargin?: number;
  /** Default button padding */
  buttonPadding?: number;
  /** Default labels */
  labels?: Partial<Record<PlatformId, string>>;
  /** Default z-index */
  zIndex?: number;
  /** Default auto width threshold */
  autoWidthThreshold?: number;
  /** Default layout mode */
  mode?: LayoutMode;
  /** Default button shape */
  shape?: ButtonShape;
  /** Default block mode configuration */
  blockConfig?: BlockShareConfig;
}

/** Options for creating a sticky bar */
export interface StickyBarOptions extends Partial<Omit<SocialShareConfig, 'position' | 'target'>> {
  /** Position for sticky bar: 'auto', 'left', or 'bottom' */
  position?: Exclude<PositionMode, 'static'>;
}

/** Options for creating an inline share bar */
export interface InlineOptions extends Partial<Omit<SocialShareConfig, 'position' | 'target'>> {
  /** Additional options for inline instances */
}

/**
 * ShareKit Manager Class
 * Central controller for creating and managing share bar instances.
 * 
 * @example
 * ```ts
 * import { ShareKit } from 'sharekit';
 * 
 * // Configure global defaults
 * ShareKit.configure({
 *   platforms: ['twitter', 'facebook', 'linkedin', 'copylink'],
 *   content: {
 *     url: 'https://example.com',
 *     title: 'Check this out!'
 *   }
 * });
 * 
 * // Create instances
 * const sticky = ShareKit.createStickyBar({ position: 'left' });
 * const inline = ShareKit.createInline('#share-buttons');
 * 
 * // Update all instances at once
 * ShareKit.updateContent({ url: 'https://example.com/new-page' });
 * ```
 */
class ShareKitManager {
  private globalConfig: ShareKitGlobalConfig = {};
  private instances: Set<ShareInstance> = new Set();

  constructor() {
    if (typeof window !== 'undefined') {
      console.log(
        `%c ShareKit v${__VERSION__} %c Build: ${__BUILD_TIME__} `,
        'background:#764ba2; color:#fff; padding:2px 5px; border-radius:3px 0 0 3px; font-weight:bold;',
        'background:#667eea; color:#fff; padding:2px 5px; border-radius:0 3px 3px 0;'
      );
    }
  }

  /**
   * Configure global defaults for all ShareKit instances
   * @param config - Global configuration options
   */
  configure(config: ShareKitGlobalConfig): this {
    this.globalConfig = {
      ...this.globalConfig,
      ...config,
      content: {
        ...this.globalConfig.content,
        ...config.content,
      },
      labels: {
        ...this.globalConfig.labels,
        ...config.labels,
      },
    };
    return this;
  }

  /**
   * Get current global configuration
   */
  getConfig(): ShareKitGlobalConfig {
    return { ...this.globalConfig };
  }

  /**
   * Create a sticky share bar (fixed position on screen)
   * @param options - Configuration options for the sticky bar
   * @returns ShareInstance
   * 
   * @example
   * ```ts
   * // Auto position (left on desktop, bottom on mobile)
   * const sticky = ShareKit.createStickyBar();
   * 
   * // Fixed left position
   * const left = ShareKit.createStickyBar({ position: 'left' });
   * 
   * // Fixed bottom position
   * const bottom = ShareKit.createStickyBar({ position: 'bottom' });
   * ```
   */
  createStickyBar(options: StickyBarOptions = {}): ShareInstance {
    const mergedConfig = this.mergeWithGlobal({
      ...options,
      position: options.position || 'auto',
      skipAutoMount: false,
    });

    const instance = new ShareInstance(mergedConfig);
    this.instances.add(instance);

    return instance;
  }

  /**
   * Create an inline share bar (embedded in a target element)
   * @param target - CSS selector or HTMLElement to mount the share bar
   * @param options - Configuration options for the inline bar
   * @returns ShareInstance
   * 
   * @example
   * ```ts
   * // Mount to a selector
   * const inline = ShareKit.createInline('#share-buttons');
   * 
   * // Mount to an element with custom platforms
   * const element = document.getElementById('shares');
   * const inline = ShareKit.createInline(element, {
   *   platforms: ['twitter', 'facebook', 'copylink']
   * });
   * ```
   */
  createInline(target: string | HTMLElement, options: InlineOptions = {}): ShareInstance {
    const targetSelector = typeof target === 'string' 
      ? target 
      : undefined;

    const mergedConfig = this.mergeWithGlobal({
      ...options,
      position: 'static' as const,
      target: targetSelector,
      skipAutoMount: true, // We'll mount manually
    });

    const instance = new ShareInstance(mergedConfig);
    this.instances.add(instance);

    // Mount to the target
    if (typeof document !== 'undefined') {
      const doMount = () => {
        instance.mount(target);
        instance.show();
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', doMount);
      } else {
        doMount();
      }
    }

    return instance;
  }

  /**
   * Create a standalone ShareInstance with full configuration control
   * @param config - Full configuration for the instance
   * @returns ShareInstance
   * 
   * @example
   * ```ts
   * const instance = ShareKit.create({
   *   platforms: ['twitter', 'facebook'],
   *   position: 'static',
   *   target: '#my-share-bar',
   *   content: { url: 'https://example.com' }
   * });
   * ```
   */
  create(config: SocialShareConfig): ShareInstance {
    const mergedConfig = this.mergeWithGlobal({
      ...config,
      skipAutoMount: false,
    });

    const instance = new ShareInstance(mergedConfig);
    this.instances.add(instance);

    return instance;
  }

  /**
   * Update content across all managed instances
   * @param content - New content to share
   * 
   * @example
   * ```ts
   * // Update URL for SPA navigation
   * ShareKit.updateContent({
   *   url: window.location.href,
   *   title: document.title
   * });
   * ```
   */
  updateContent(content: Partial<ShareContent>): this {
    // Update global config
    this.globalConfig.content = {
      ...this.globalConfig.content,
      ...content,
    };

    // Update all instances
    this.instances.forEach(instance => {
      instance.update(content);
    });

    return this;
  }

  /**
   * Get all managed instances
   * @returns Array of ShareInstance objects
   */
  getInstances(): ShareInstance[] {
    return Array.from(this.instances);
  }

  /**
   * Get the number of managed instances
   */
  get instanceCount(): number {
    return this.instances.size;
  }

  /**
   * Remove an instance from management (does not destroy it)
   * @param instance - The instance to remove
   */
  removeInstance(instance: ShareInstance): this {
    this.instances.delete(instance);
    return this;
  }

  /**
   * Destroy an instance and remove it from management
   * @param instance - The instance to destroy
   */
  destroyInstance(instance: ShareInstance): this {
    instance.destroy();
    this.instances.delete(instance);
    return this;
  }

  /**
   * Destroy all managed instances
   */
  destroyAll(): this {
    this.instances.forEach(instance => {
      instance.destroy();
    });
    this.instances.clear();
    return this;
  }

  /**
   * Reset global configuration to defaults
   */
  reset(): this {
    this.globalConfig = {};
    return this;
  }

  /**
   * Create a "Smart Block" instance that handles P1/P2 layout based on container width.
   * 
   * Layout Logic (calculated once at initialization):
   * - P1[0]: Always expanded (Icon + Text, Flex Grow)
   * - P1[1+]: Expanded if container > threshold, otherwise icon-only
   * - P2[*]: Always icon-only
   * 
   * Note: Layout is determined at creation time. If container width changes,
   * recreate the SmartBlock or refresh the page.
   */
  createSmartBlock(config: SmartBlockConfig): ShareInstance {
    const { 
      target, 
      p1, 
      p2 = [], 
      threshold = 640, 
      height = 50, 
      gap = 2, 
      radius = 0 
    } = config;

    // Get container element
    const containerEl = typeof target === 'string' 
      ? document.querySelector(target) as HTMLElement
      : target as HTMLElement;
    
    // Get width from parent element (the actual container with defined width)
    // Fall back to container itself, then window width
    const parentEl = containerEl?.parentElement;
    const containerWidth = parentEl?.offsetWidth 
      || containerEl?.offsetWidth 
      || window.innerWidth;
    const isWide = containerWidth > threshold;

    // Build platform list: p1 + p2 + copylink (auto-appended)
    const allPlatforms: PlatformId[] = [...p1, ...p2];
    if (!allPlatforms.includes('copylink')) {
      allPlatforms.push('copylink');
    }

    // Build items configuration based on current width
    const items = this.buildSmartBlockItems(p1, p2, isWide);
    // Ensure copylink is always icon-only
    items['copylink'] = { grow: false, labelState: 'never' };

    // Create instance with calculated configuration
    return this.createInline(target, {
      platforms: allPlatforms,
      mode: 'block' as const,
      shape: radius === 0 ? 'square' as const : 'rounded' as const,
      blockConfig: { height, gap, radius, items }
    });
  }

  /**
   * Build SmartBlock items configuration
   * @private
   */
  private buildSmartBlockItems(
    p1: PlatformId[], 
    p2: PlatformId[], 
    isWide: boolean
  ): Record<string, { grow: boolean; labelState: 'always' | 'never' }> {
    const items: Record<string, { grow: boolean; labelState: 'always' | 'never' }> = {};

    // P1 Logic: First always expands, others expand only if wide
    p1.forEach((id, index) => {
      const shouldExpand = index === 0 || isWide;
      items[id] = {
        grow: shouldExpand,
        labelState: shouldExpand ? 'always' : 'never',
      };
    });

    // P2 Logic: Always icon-only
    p2.forEach(id => {
      items[id] = {
        grow: false,
        labelState: 'never'
      };
    });

    return items;
  }

  /**
   * Merge instance options with global defaults
   */
  private mergeWithGlobal(options: Partial<ShareInstanceConfig>): ShareInstanceConfig {
    const merged: ShareInstanceConfig = {
      // Start with default platforms if none specified
      platforms: options.platforms || this.globalConfig.platforms || DEFAULT_CONFIG.platforms,
      
      // Merge content
      content: {
        ...DEFAULT_CONFIG.content,
        ...this.globalConfig.content,
        ...options.content,
      },

      // Apply other global defaults - chain ?? DEFAULT_CONFIG to guarantee a value
      iconSpritePath: options.iconSpritePath ?? this.globalConfig.iconSpritePath ?? DEFAULT_CONFIG.iconSpritePath,
      iconSize: options.iconSize ?? this.globalConfig.iconSize ?? DEFAULT_CONFIG.iconSize,
      buttonOpacity: options.buttonOpacity ?? this.globalConfig.buttonOpacity ?? DEFAULT_CONFIG.buttonOpacity,
      buttonMargin: options.buttonMargin ?? this.globalConfig.buttonMargin ?? DEFAULT_CONFIG.buttonMargin,
      buttonPadding: options.buttonPadding ?? this.globalConfig.buttonPadding ?? DEFAULT_CONFIG.buttonPadding,
      zIndex: options.zIndex ?? this.globalConfig.zIndex ?? DEFAULT_CONFIG.zIndex,
      autoWidthThreshold: options.autoWidthThreshold ?? this.globalConfig.autoWidthThreshold ?? DEFAULT_CONFIG.autoWidthThreshold,
      mode: options.mode ?? this.globalConfig.mode ?? DEFAULT_CONFIG.mode,
      shape: options.shape ?? this.globalConfig.shape ?? DEFAULT_CONFIG.shape,

      // Merge labels
      labels: {
        ...DEFAULT_LABELS,
        ...this.globalConfig.labels,
        ...options.labels,
      },

      // Merge block config
      blockConfig: {
        ...this.globalConfig.blockConfig,
        ...options.blockConfig,
        items: {
          ...this.globalConfig.blockConfig?.items,
          ...options.blockConfig?.items,
        },
      },

      // Pass through instance-specific options
      position: options.position,
      target: options.target,
      onShare: options.onShare,
      onCopy: options.onCopy,
      skipAutoMount: options.skipAutoMount,
    };

    return merged;
  }
}

// Export singleton instance
export const ShareKit = new ShareKitManager();

// Also export the class for advanced usage
export { ShareKitManager };

